import { PageHeaderPrimitives } from '@uhg-abyss/web/ui/PageHeader';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { event } from '@uhg-abyss/web/tools/event';
import { useRouter } from '@uhg-abyss/web/hooks/useRouter';
import { storage } from '@uhg-abyss/web/tools/storage';
import { config } from '@uhg-abyss/web/tools/config';
import { leftNavData, leftNavMap } from '@src/common/Utils/utils';
import { useMediaQuery } from '@uhg-abyss/web/hooks/useMediaQuery';
import { LagoonContext } from '../../AppContext';
import { LogoutSuccessModal } from './LogoutSuccessModal/LogoutSuccessModal';
import { SubscribeToAPIModal } from '@src/common/SubscribeToAPIModal';
import { resetUserInfo, useUserInfo } from '@src/hooks/useUserInfo';
import { DesktopHeader } from './DesktopHeader';
import { MobileHeader } from './MobileHeader';

export const NewHeader = () => {
  const { navigate } = useRouter();
  const [subMenuTitle, setSubMenuTitle] = useState('');
  const [subMenuInfo, setSubMenuInfo] = useState([]);
  const [isOpen, setIsOpen] = useState(false);

  const userInfo = useSelector((state) => {
    return state.GET_USER_INFO.data;
  });

  const lagoonContext = React.useContext(LagoonContext);
  const [showSubscribeAPIModal, setShowSubscribeAPIModal] = useState(false);
  const [showLogoutSuccessModal, setshowLogoutSuccessModal] = useState(false);
  const [userInfoReset, runResetUserInfo] = resetUserInfo();
  const isLogInClicked = storage.local.get('IS_SIGN_IN_CLICKED');
  const [, getUserInfo] = useUserInfo();

  const isMobile = useMediaQuery('(max-width: 480px)'); // For mobile devices
  const isTablet = useMediaQuery('(max-width: 820px)'); // For tablets
  const isDesktop = useMediaQuery('(min-width: 821px)'); // For desktops

  const [isLoggedIn, setIsLoggedIn] = useState(false);

  const leftNavHeaders = lagoonContext
    ?.getTable('knowledge-base-left-nav-headers')
    .getRow();
  const leftNavHeaderItems = lagoonContext
    ?.getTable('knowledge-base-header-items-v2')
    .getRow();
  const leftNavHeadersSub = lagoonContext
    ?.getTable('header-api-library-submenu')
    .getRow();

  let leftNavContent = leftNavHeaders;
  const leftNavContentSub = leftNavHeadersSub;
  const leftHeaderItems = leftNavHeaderItems;

  leftNavContent = leftNavMap(
    leftNavContent,
    leftHeaderItems,
    leftNavContentSub
  );

  useEffect(() => {
    if (userInfo?.firstName) {
      setIsLoggedIn(true);
    } else {
      setIsLoggedIn(false);
    }
  }, [userInfo]);

  const fireHeaderEvent = (
    interactionValue,
    interactionName,
    interactionContext = 'sub header link click'
  ) => {
    event('INTERACTION_EVENT', {
      interactionName,
      interactionValue,
      interactionContext,
    });
  };

  const routeToPage = ({
    tagging,
    interactionName,
    interactionContext,
    url,
  }) => {
    if (tagging) {
      const interactionValue = 'api technical documentation';
      event('INTERACTION_EVENT', {
        interactionName,
        interactionValue,
        interactionContext,
      });
    }
    setIsOpen(false);
    navigate(url);
  };

  const filteredLeftNavData = () => {
    const knowledgeBaseData = leftNavData(
      userInfo,
      leftNavContent,
      leftHeaderItems,
      leftNavContentSub
    );
    return knowledgeBaseData;
  };

  const updateUtilityMenu = (title, item) => {
    setSubMenuTitle(title);
    setSubMenuInfo(item);
  };

  const signIn = () => {
    setIsOpen(false);
    const interactionName = `welcome`;
    const interactionValue = 'welcome';
    const interactionContext = '';
    event('INTERACTION_EVENT', {
      interactionName,
      interactionValue,
      interactionContext,
    });

    storage.local.set('IS_SIGN_IN_CLICKED', true);
    if (storage.local.get('IS_SIGNED_OUT')) {
      storage.local.remove('IS_SIGNED_OUT');
    }

    if (process.env.NODE_ENV === 'development') {
      navigate('/welcome');
    } else {
      window.location.href = `${config('IDENTITY_API_URL')}/login`;
    }
  };

  const signOut = () => {
    storage.local.set('IS_SIGNED_OUT', true);
    sessionStorage.removeItem('SECRET_EXPIRY_CHECKED');
    runResetUserInfo();
    setshowLogoutSuccessModal(true);
    window.location.href = `${config('IDENTITY_API_URL')}/logout`;
    const modalName = 'sign out';
    event('MODAL_VIEW', {
      event,
      modalName,
    });
    event('MODAL_CLOSE', {
      event,
      modalName,
    });
  };

  useEffect(() => {
    const handleInvalidToken = (e) => {
      if (e.key === 'IS_SIGNED_OUT' && storage.local.get('IS_SIGNED_OUT')) {
        setIsLoggedIn(false);
        signOut();
      }
    };
    window.addEventListener('storage', handleInvalidToken);
    return function cleanup() {
      window.removeEventListener('storage', handleInvalidToken);
    };
  }, [userInfoReset]);

  useEffect(() => {
    if (
      document.referrer.includes('identity') &&
      !storage.local.get('IS_SIGNED_OUT')
    ) {
      getUserInfo();
      if (isLogInClicked) {
        navigate('/welcome');
      }
    }
  }, [document.referrer]);

  // adding some memo related refactoring
  const priorities = {
    overview: 1,
    'technical demo videos': 2,
    'frequently asked questions': 3,
    'technical documentation': 4,
    // Default priority for other paths
    default: 100,
  };

  // Memoize the path priority function
  const getPathPriority = useCallback(
    (pathName) => {
      if (!pathName) return priorities.default;

      const pathNameLower =
        typeof pathName === 'string' ? pathName.toLowerCase() : '';

      // Find matching priority or use default
      const matchingKey = Object.keys(priorities).find((key) => {
        return pathNameLower.includes(key);
      });
      return matchingKey ? priorities[matchingKey] : priorities.default;
    },
    [priorities]
  );

  const filterAndSortPaths = useCallback(
    (pathsData) => {
      if (!Array.isArray(pathsData)) return [];

      return pathsData
        .filter((el) => {
          return (
            el &&
            el.tag !== 'Overview & Onboarding' &&
            el.tag !== 'Getting Started'
          );
        })
        .sort((a, b) => {
          if (!a || !b || !a.tag || !b.tag) return 0;

          // Sort by priority first, then alphabetically
          const priorityA = getPathPriority(a.tag.toLowerCase());
          const priorityB = getPathPriority(b.tag.toLowerCase());

          if (priorityA !== priorityB) {
            return priorityA - priorityB;
          }

          // If same priority, sort alphabetically
          return a.tag.localeCompare(b.tag);
        })
        .map((tagGroup) => {
          if (!tagGroup) return null;
          if (tagGroup.tag === 'Overview & Onboarding') {
            return tagGroup;
          }

          return {
            ...tagGroup,
            paths: Array.isArray(tagGroup.paths)
              ? [...tagGroup.paths].sort((a, b) => {
                  if (!a || !b || !a.name || !b.name) return 0;

                  const priorityA = getPathPriority(a.name);
                  const priorityB = getPathPriority(b.name);

                  if (priorityA !== priorityB) {
                    return priorityA - priorityB;
                  }

                  // If same priority, sort alphabetically
                  return a.name.localeCompare(b.name);
                })
              : [],
          };
        })
        .filter(Boolean); // Remove any nulls that might have been introduced
    },
    [getPathPriority]
  );

  // Memoize the drawer link creation function
  const createDrawerLink = useCallback(
    (el, isMobileMenuValue) => {
      if (!el) return null;

      const { tag = '' } = el || {};
      const handleClick = () => {
        fireHeaderEvent(tag, 'api library');
        return updateUtilityMenu(tag, el);
      };

      return (
        <PageHeaderPrimitives.DrawerLink
          style={{
            flex: '1 1 48%', // Each item takes up ~48% of the row
            margin: '0', // Remove extra margin if needed
          }}
          css={{
            '.abyss-insert-element-root, , .abyss-insert-element-root *': {
              color: '#196ECF !important',
            },
            '.abyss-page-header-drawer-menu-item-button': {
              width: '450px',
            },
          }}
          onClick={handleClick}
          title={tag}
          key={`drawer-${el.id || tag}`}
          path={
            isMobileMenuValue
              ? '/home/API Library/submenu'
              : '/knowledgebase/submenu'
          }
        />
      );
    },
    [fireHeaderEvent, updateUtilityMenu]
  );

  // Main memoized getPathsByTagInfo function
  const getPathsByTagInfo = useCallback(
    (pathsByTag, isMobileMenu) => {
      if (!pathsByTag) return null;

      // Use useMemo for the expensive sorting operation
      const filteredAndSortedPaths = useMemo(() => {
        return filterAndSortPaths(pathsByTag);
      }, [pathsByTag, filterAndSortPaths]);

      // Use useMemo for the component rendering
      return useMemo(() => {
        return (
          <React.Fragment>
            {(isMobile || isTablet) &&
              filteredAndSortedPaths.map((el) => {
                return createDrawerLink(el, isMobileMenu);
              })}
            {isDesktop && (
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  justifyContent: 'space-between',
                  gap: '1%',
                }}
              >
                {filteredAndSortedPaths.map((el) => {
                  return createDrawerLink(el, isMobileMenu);
                })}
              </div>
            )}
          </React.Fragment>
        );
      }, [
        filteredAndSortedPaths,
        isMobile,
        isTablet,
        isDesktop,
        createDrawerLink,
        isMobileMenu,
      ]);
    },
    [filterAndSortPaths, createDrawerLink, isMobile, isTablet, isDesktop]
  );

  const closeAllModals = () => {
    setshowLogoutSuccessModal(false);
  };

  return (
    <React.Fragment>
      <SubscribeToAPIModal
        showModal={showSubscribeAPIModal}
        modalOnClose={setShowSubscribeAPIModal}
      />
      {(isMobile || isTablet) && (
        <MobileHeader
          signIn={signIn}
          signOut={signOut}
          fireHeaderEvent={fireHeaderEvent}
          routeToPage={routeToPage}
          setShowSubscribeAPIModal={setShowSubscribeAPIModal}
          isLoggedIn={isLoggedIn}
          filteredLeftNavData={filteredLeftNavData}
          getPathsByTagInfo={getPathsByTagInfo}
          subMenuTitle={subMenuTitle}
          subMenuInfo={subMenuInfo}
          isOpen={isOpen}
          setIsOpen={setIsOpen}
        />
      )}
      {isDesktop && (
        <DesktopHeader
          signIn={signIn}
          signOut={signOut}
          fireHeaderEvent={fireHeaderEvent}
          routeToPage={routeToPage}
          setShowSubscribeAPIModal={setShowSubscribeAPIModal}
          isLoggedIn={isLoggedIn}
          filteredLeftNavData={filteredLeftNavData}
          getPathsByTagInfo={getPathsByTagInfo}
          subMenuTitle={subMenuTitle}
          subMenuInfo={subMenuInfo}
        />
      )}
      {showLogoutSuccessModal && (
        <LogoutSuccessModal closeAllModals={closeAllModals} />
      )}
    </React.Fragment>
  );
};
